// deps
import React, { useEffect, useState, useCallback, ReactElement, memo, useMemo, useContext } from "react";

// internal deps
import { TickScreen, TICK_STATES } from "@swiggy-private/payment-ui";
import {
    CheckoutContext,
    getBridgeSdk,
    isToingFlow,
    NEWRELIC_TABLES,
    recordNREvent,
} from "@swiggy-private/checkout-core";
import { useEffectOnce } from "@swiggy-private/core-ui";

// hooks
import UseDirectPlaceOrder from "@payment-client/hooks/UseDirectPlaceOrder";

import { isSuperUserOrder } from "@payment-client/helpers/OrderHelper";
import { postOrderEventHelper } from "@payment-client/helpers/PostOrderHelper";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";
import { getBusinessLine } from "@payment-client/helpers/Misc";
import { getConfirmOrderTexts } from "@payment-client/helpers/PaymentHelper";
import { TEXT_TYPES } from "@payment-client/constants/ConfirmScreen";
import { getSubTextNodeList } from "@payment-client/helpers/OrderConfirmHelper";
import { DirectPlaceOrderPayload } from "@swiggy-private/payment-types";

export const DirectPlaceOrder = memo(function DirectPlaceOrder(_props: RouteProps): ReactElement {
    const { checkoutState } = useContext(CheckoutContext);
    const {
        cartTotal = 0,
        fromCart = false,
        orderComment = "",
    } = (checkoutState?.payload as DirectPlaceOrderPayload) || {};
    const [directPlaceOrderState, doDirectPlaceOrder] = UseDirectPlaceOrder({ cartTotal, fromCart, orderComment });
    const { isDirectPlaceOrderSuccess, orderData } = directPlaceOrderState;
    const initialState = isDirectPlaceOrderSuccess ? TICK_STATES.SUCCESS : TICK_STATES.LOADING;
    const [state, setState] = useState<TICK_STATES>(initialState);
    const deliveryAddress = orderData?.deliveryAddress;

    // Logic to handle post placing successful order
    const postDirectPlaceOrderSuccessHandler = useCallback((): void => {
        if (orderData == null) {
            getBridgeSdk()
                .directPlaceOrderFailure()
                .catch(() => void 0);
            return;
        }

        recordNREvent({ eventType: NEWRELIC_TABLES.PAYMENT_WEB_DIRECT_PLACE_ORDER, fromCart: String(fromCart) }, false);

        postOrderEventHelper(orderData.rawResponse.order_data, true);
    }, [fromCart, orderData]);

    const isSuper = useCallback((): boolean => {
        if (orderData == null) {
            return false;
        }
        return isSuperUserOrder(orderData);
    }, [orderData]);

    const getSuperMessage = useCallback(() => {
        if (!isSuper() || orderData == null) {
            return;
        }
        let successMessage = "";
        let successMessageInfo = "";

        if (orderData.successMessage) {
            successMessage = orderData.successMessage;
            successMessageInfo = orderData.successMessageInfo ?? "";
        }
        return {
            successMessage,
            successMessageInfo,
        };
    }, [orderData, isSuper]);

    useEffect(() => {
        if (state != TICK_STATES.SUCCESS && isDirectPlaceOrderSuccess) {
            setState(TICK_STATES.SUCCESS);
        }
    }, [isDirectPlaceOrderSuccess, state]);

    useEffectOnce(() => {
        doDirectPlaceOrder();
    });

    const businessLine = getBusinessLine();
    const isToing = useMemo(() => isToingFlow(), []);
    const successData = useMemo(
        () => getConfirmOrderTexts({ businessLine, tickState: state, isToing }),
        [state, businessLine, isToing],
    );
    const mainText = successData[TEXT_TYPES.MAIN];
    const subText = successData[TEXT_TYPES.SUB];

    const subTextNodeList = useMemo(
        () =>
            getSubTextNodeList({
                state,
                subText,
                deliveryAddress,
                getSuperMessageCb: getSuperMessage,
            }),
        [deliveryAddress, getSuperMessage, state, subText],
    );

    return (
        <TickScreen
            tickState={state}
            mainText={mainText}
            subTextNodeList={subTextNodeList}
            isSuper={isSuper()}
            postSuccessCb={postDirectPlaceOrderSuccessHandler}
        />
    );
});
